import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './styles/DocHubFilter.scss';

const DocHubFilter = ({ filters, onFiltersChange, onClose }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Document types
  const documentTypes = [
    { value: 'certificate', label: 'Certificate' },
    { value: 'policy', label: 'Policy' },
    { value: 'endorsement', label: 'Endorsement' },
    { value: 'document', label: 'Other' },
  ];
  
  // Status options
  const statusOptions = [
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REVIEW', label: 'Review' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'EXPIRED', label: 'Expired' },
    { value: 'REJECTED', label: 'Rejected' },
  ];
  
  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All' },
    { value: 'last30', label: 'Last 30 days' },
    { value: 'last60', label: 'Last 60 days' },
    { value: 'last90', label: 'Last 90 days' },
    { value: 'custom', label: 'Custom Range' },
  ];
  
  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle checkbox changes
  const handleCheckboxChange = (field, value, checked) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(v => v !== value)
    }));
  };
  
  // Apply filters
  const handleApply = () => {
    onFiltersChange(localFilters);
  };
  
  // Reset filters
  const handleReset = () => {
    const resetFilters = {
      dateRange: 'all',
      dateFrom: '',
      dateTo: '',
      status: [],
      subcontractor: '',
      documentType: [],
      expirationRange: 'all',
      expirationFrom: '',
      expirationTo: '',
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };
  
  return (
    <Box className="dochub-filter">
        <Box className="dochub-filter-header">
          <Typography variant="h6">Filters</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Grid container spacing={3} className="dochub-filter-content">
          {/* Upload Date */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <FormLabel component="legend">Upload Date</FormLabel>
              <Select
                value={localFilters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                size="small"
              >
                {dateRangeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              
              {localFilters.dateRange === 'custom' && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>From</Typography>
                    <DatePicker
                      selected={localFilters.dateFrom || null}
                      onChange={(date) => handleFilterChange('dateFrom', date)}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      placeholderText="Select date"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>To</Typography>
                    <DatePicker
                      selected={localFilters.dateTo || null}
                      onChange={(date) => handleFilterChange('dateTo', date)}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      placeholderText="Select date"
                    />
                  </Box>
                </Box>
              )}
            </FormControl>
          </Grid>
          
          {/* Document Type */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Document Type</FormLabel>
              <FormGroup>
                {documentTypes.map(type => (
                  <FormControlLabel
                    key={type.value}
                    control={
                      <Checkbox
                        checked={localFilters.documentType.includes(type.value)}
                        onChange={(e) => handleCheckboxChange('documentType', type.value, e.target.checked)}
                        size="small"
                      />
                    }
                    label={type.label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
          
          {/* Status */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Status</FormLabel>
              <FormGroup>
                {statusOptions.map(status => (
                  <FormControlLabel
                    key={status.value}
                    control={
                      <Checkbox
                        checked={localFilters.status.includes(status.value)}
                        onChange={(e) => handleCheckboxChange('status', status.value, e.target.checked)}
                        size="small"
                      />
                    }
                    label={status.label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
          
          {/* Subcontractor & Expiration */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel>Subcontractor</FormLabel>
              <TextField
                placeholder="Enter subcontractor name"
                value={localFilters.subcontractor}
                onChange={(e) => handleFilterChange('subcontractor', e.target.value)}
                size="small"
                fullWidth
              />
            </FormControl>
            
            <FormControl fullWidth>
              <FormLabel>Expiration Date</FormLabel>
              <Select
                value={localFilters.expirationRange}
                onChange={(e) => handleFilterChange('expirationRange', e.target.value)}
                size="small"
              >
                {dateRangeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              
              {localFilters.expirationRange === 'custom' && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>From</Typography>
                    <DatePicker
                      selected={localFilters.expirationFrom || null}
                      onChange={(date) => handleFilterChange('expirationFrom', date)}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      placeholderText="Select date"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>To</Typography>
                    <DatePicker
                      selected={localFilters.expirationTo || null}
                      onChange={(date) => handleFilterChange('expirationTo', date)}
                      dateFormat="MM/dd/yyyy"
                      className="form-control"
                      placeholderText="Select date"
                    />
                  </Box>
                </Box>
              )}
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Filter Actions */}
        <Box className="dochub-filter-actions">
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            variant="outlined"
          >
            Reset
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
          >
            Apply
          </Button>
        </Box>
      </Box>
  );
};

export default DocHubFilter;